import { Action, createReducer, on } from '@ngrx/store';
import { IPagination } from 'src/app/shared/models/page.model';
import {
  IWorkflowRuleSummary,
  IWorkflowSchema,
  IWorkflowTemplate,
} from '../models/workflows.models';
import * as workflowsActions from './workflows.actions';

// ─── WorkflowsState ───────────────────────────────────────────────────────────

export interface WorkflowsState {
  rules: IWorkflowRuleSummary[];
  metadata: IPagination | null;
  loading: boolean;
  loadError: boolean;

  schema: IWorkflowSchema | null;
  schemaLoading: boolean;

  templates: IWorkflowTemplate[];
  templatesLoading: boolean;

  saveLoading: boolean;
  saveError: boolean;
  saveCompletedAt: number | null;
}

export const initialWorkflowsState: WorkflowsState = {
  rules: [],
  metadata: null,
  loading: false,
  loadError: false,

  schema: null,
  schemaLoading: false,

  templates: [],
  templatesLoading: false,

  saveLoading: false,
  saveError: false,
  saveCompletedAt: null,
};

// ─── Reducer ────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialWorkflowsState,

  // Rules list
  on(workflowsActions.GetWorkflowRulesSuccess, (state, { response }) => ({
    ...state,
    rules: response.data,
    metadata: response.metadata,
    loadError: false,
  })),
  on(workflowsActions.GetWorkflowRulesError, (state) => ({
    ...state,
    loadError: true,
  })),
  on(workflowsActions.SetWorkflowsLoader, (state, { isLoading }) => ({
    ...state,
    loading: isLoading,
  })),

  // Schema
  on(workflowsActions.GetWorkflowSchema, (state) => ({
    ...state,
    schemaLoading: true,
  })),
  on(workflowsActions.GetWorkflowSchemaSuccess, (state, { schema }) => ({
    ...state,
    schema,
    schemaLoading: false,
  })),
  on(workflowsActions.GetWorkflowSchemaError, (state) => ({
    ...state,
    schemaLoading: false,
  })),

  // Templates
  on(workflowsActions.GetWorkflowTemplates, (state) => ({
    ...state,
    templatesLoading: true,
  })),
  on(workflowsActions.GetWorkflowTemplatesSuccess, (state, { templates }) => ({
    ...state,
    templates,
    templatesLoading: false,
  })),
  on(workflowsActions.GetWorkflowTemplatesError, (state) => ({
    ...state,
    // Templates are an optional shortcut: on failure the section is simply
    // absent and "from scratch" still works, so no error banner.
    templates: [],
    templatesLoading: false,
  })),

  // Save rule (create / update)
  on(workflowsActions.SaveWorkflowRule, (state) => ({
    ...state,
    saveLoading: true,
    saveError: false,
  })),
  on(workflowsActions.SaveWorkflowRuleSuccess, (state, { completedAt }) => ({
    ...state,
    saveLoading: false,
    saveError: false,
    saveCompletedAt: completedAt,
  })),
  on(workflowsActions.SaveWorkflowRuleError, (state) => ({
    ...state,
    saveLoading: false,
    saveError: true,
  })),
  on(workflowsActions.ResetWorkflowSaveState, (state) => ({
    ...state,
    saveLoading: false,
    saveError: false,
    saveCompletedAt: null,
  })),

  // Delete rule
  on(workflowsActions.DeleteWorkflowRuleSuccess, (state, { id }) => ({
    ...state,
    rules: state.rules.filter((r) => r.id !== id),
  })),

  // Toggle status
  on(workflowsActions.ToggleWorkflowRuleStatusSuccess, (state, { id, status }) => ({
    ...state,
    rules: state.rules.map((r) => (r.id === id ? { ...r, status } : r)),
  }))
);

export function workflowsReducer(state: WorkflowsState | undefined, action: Action) {
  return featureReducer(state, action);
}
